import { computed, type Ref } from 'vue'

import { parseLineString } from '@/services/lineStringService'
import { shouldHideAscentForTour } from '@/services/tourTypeService'
import type { Photo } from '@/types/photo'
import type { Tour } from '@/types/tour'

const EARTH_RADIUS_M = 6_371_000
const DEG_TO_RAD = Math.PI / 180
// Cap is intentionally generous: SVG polyline at the slideshow's ~120px width
// handles thousands of vertices fine, and aggressive stride sampling visibly
// strips peaks/valleys from short tracks.
const MAX_PROFILE_POINTS = 300
// Half-window (in metres) used to compute a local grade at the photo's
// nearest route point when the source point has no `grade` field of its own.
const LOCAL_GRADE_HALF_WINDOW_M = 50

function haversineM(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = (lat2 - lat1) * DEG_TO_RAD
  const dLon = (lon2 - lon1) * DEG_TO_RAD
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * DEG_TO_RAD) * Math.cos(lat2 * DEG_TO_RAD) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_RADIUS_M * Math.asin(Math.sqrt(a))
}

export interface PhotoRouteData {
  kmOnRoute: number | null
  elevationM: number | null
  gainM: number | null
  gradientPct: number | null
  elevationProfile: Array<{ d: number; ele: number }> | null
  routePolyline: Array<[number, number]> | null
  photoPoint: [number, number] | null
}

const EMPTY: PhotoRouteData = {
  kmOnRoute: null,
  elevationM: null,
  gainM: null,
  gradientPct: null,
  elevationProfile: null,
  routePolyline: null,
  photoPoint: null,
}

interface BuiltPoint {
  lat: number
  lon: number
  ele: number | null
  grade: number | null
  time: number | null
  kmOnRoute: number
  gainM: number
}

/** Walk all tracks of a tour in order, accumulating distance and elevation gain. */
function buildTourPoints(tour: Tour): BuiltPoint[] {
  const pts: BuiltPoint[] = []
  let cumM = 0
  let gainM = 0
  let prevLat: number | null = null
  let prevLon: number | null = null
  let prevEle: number | null = null

  for (const track of tour.tracks) {
    for (const seg of track.segments) {
      for (const pt of seg.points) {
        if (!Number.isFinite(pt.lat) || !Number.isFinite(pt.lon)) continue

        if (prevLat !== null && prevLon !== null) {
          cumM += haversineM(prevLat, prevLon, pt.lat, pt.lon)
        }

        const ele = typeof pt.ele === 'number' && Number.isFinite(pt.ele) ? pt.ele : null
        if (ele !== null && prevEle !== null && ele > prevEle) {
          gainM += ele - prevEle
        }

        pts.push({
          lat: pt.lat,
          lon: pt.lon,
          ele,
          grade: typeof pt.grade === 'number' ? pt.grade : null,
          time: typeof pt.time === 'number' && Number.isFinite(pt.time) ? pt.time : null,
          // Always use cumM — accumulated_distance resets per track and would
          // fold the profile back to 0 for every subsequent track in a tour.
          kmOnRoute: cumM / 1000,
          gainM,
        })

        prevLat = pt.lat
        prevLon = pt.lon
        prevEle = ele
      }
    }
  }

  return pts
}

/** Build a single merged polyline from all tracks of a tour for the mini map.
 *  Prefers the 5m-reduced line over the 500m-reduced one: at the mini-map's
 *  small size the 500m line collapses short tracks to a handful of chunky
 *  zigzag segments, while the 5m line stays smooth and canvas handles the
 *  extra points trivially. */
function buildTourPolyline(tour: Tour): Array<[number, number]> | null {
  const segments: Array<[number, number]>[] = []

  for (const track of tour.tracks) {
    const reduced = track.pointsLineReduced5m ?? track.pointsLineReduced500m
    if (reduced) {
      const parsed = parseLineString(reduced)
      if (parsed.length >= 2) segments.push(parsed)
    }
  }

  if (segments.length === 0) return null
  return segments.flat()
}

export function usePhotoRouteData(
  photo: Ref<Photo | undefined>,
  tours: Ref<Tour[]>,
): Ref<PhotoRouteData> {
  return computed(() => {
    const p = photo.value
    if (!p?.lat || !p?.lon) return EMPTY

    const photoLat = p.lat
    const photoLon = p.lon

    // Phase 1: find which tour the photo belongs to (nearest any segment point)
    let bestDistM = Infinity
    let bestTour: Tour | null = null

    for (const tour of tours.value) {
      for (const track of tour.tracks) {
        for (const seg of track.segments) {
          for (const pt of seg.points) {
            if (!Number.isFinite(pt.lat) || !Number.isFinite(pt.lon)) continue
            const d = haversineM(photoLat, photoLon, pt.lat, pt.lon)
            if (d < bestDistM) {
              bestDistM = d
              bestTour = tour
            }
          }
        }
      }
    }

    if (!bestTour) return EMPTY

    // Phase 2: build full point list from ALL tracks of the matched tour so that
    // the elevation profile and minimap are always the same shape regardless of
    // which individual track segment the photo happens to be nearest to.
    const pts = buildTourPoints(bestTour)
    if (pts.length === 0) return EMPTY

    // Find spatially nearest point within the full tour point list
    let nearestIdx = 0
    let nearestDist = Infinity
    for (let i = 0; i < pts.length; i++) {
      const d = haversineM(photoLat, photoLon, pts[i].lat, pts[i].lon)
      if (d < nearestDist) {
        nearestDist = d
        nearestIdx = i
      }
    }

    // For tours that revisit the same path (out-and-back, loops, repeats), the
    // spatial nearest is ambiguous: two passes overlap within GPS noise and the
    // wrong leg may "win" by a fraction of a metre, jumping the reported km to
    // a far point on the route. If the photo has a timestamp and the track has
    // point times, prefer the time-closest candidate within a small spatial
    // band around the spatial nearest.
    const takenAt = typeof p.takenAt === 'number' && Number.isFinite(p.takenAt) ? p.takenAt : null
    if (takenAt !== null) {
      const bandM = Math.max(nearestDist + 30, nearestDist * 2)
      let bestTimeDelta = Infinity
      for (let i = 0; i < pts.length; i++) {
        const t = pts[i].time
        if (t === null) continue
        const d = haversineM(photoLat, photoLon, pts[i].lat, pts[i].lon)
        if (d > bandM) continue
        const td = Math.abs(takenAt - t)
        if (td < bestTimeDelta) {
          bestTimeDelta = td
          nearestIdx = i
        }
      }
    }

    const nearest = pts[nearestIdx]

    // If the source point lacks an explicit grade (typical for BRouter routes
    // and most GPX tracks), compute one locally from the elevation curve over
    // a small distance window centred on the photo's position.
    let gradientPct = nearest.grade
    if (gradientPct === null && nearest.ele !== null) {
      const targetKm = nearest.kmOnRoute
      const halfKm = LOCAL_GRADE_HALF_WINDOW_M / 1000
      let lo: BuiltPoint | null = null
      let hi: BuiltPoint | null = null
      for (let i = nearestIdx; i >= 0; i--) {
        if (pts[i].ele !== null && targetKm - pts[i].kmOnRoute >= halfKm) {
          lo = pts[i]
          break
        }
        if (pts[i].ele !== null) lo = pts[i]
      }
      for (let i = nearestIdx; i < pts.length; i++) {
        if (pts[i].ele !== null && pts[i].kmOnRoute - targetKm >= halfKm) {
          hi = pts[i]
          break
        }
        if (pts[i].ele !== null) hi = pts[i]
      }
      if (lo && hi && lo.ele !== null && hi.ele !== null) {
        const distKm = hi.kmOnRoute - lo.kmOnRoute
        if (distKm > 0) {
          gradientPct = ((hi.ele - lo.ele) / (distKm * 1000)) * 100
        }
      }
    }

    // Elevation profile — subsample to keep rendering fast
    const withEle = pts.filter((pt): pt is BuiltPoint & { ele: number } => pt.ele !== null)
    let elevationProfile: PhotoRouteData['elevationProfile'] = null
    if (withEle.length >= 2) {
      const step = Math.max(1, Math.floor(withEle.length / MAX_PROFILE_POINTS))
      const sampled: Array<{ d: number; ele: number }> = []
      for (let i = 0; i < withEle.length; i += step) {
        sampled.push({ d: withEle[i].kmOnRoute, ele: withEle[i].ele })
      }
      const last = withEle[withEle.length - 1]
      if (sampled[sampled.length - 1].d !== last.kmOnRoute) {
        sampled.push({ d: last.kmOnRoute, ele: last.ele })
      }
      elevationProfile = sampled
    }

    // Mini map — merge all tracks of the tour into one polyline
    const routePolyline =
      buildTourPolyline(bestTour) ?? pts.map((pt) => [pt.lon, pt.lat] as [number, number])

    const hideElevationStats = shouldHideAscentForTour(bestTour)

    return {
      kmOnRoute: nearest.kmOnRoute,
      elevationM: hideElevationStats ? null : nearest.ele,
      gainM: hideElevationStats ? null : nearest.gainM,
      gradientPct: hideElevationStats ? null : gradientPct,
      elevationProfile: hideElevationStats ? null : elevationProfile,
      routePolyline: routePolyline.length >= 2 ? routePolyline : null,
      photoPoint: [photoLon, photoLat],
    }
  })
}
